import {
    type DosageData,
    MedicationForm,
    MedicationFormMap,
    MedicationVolume,
    MedicationVolumeMap,
} from "@qendo/medication";
import type { MapEntry } from "@qendo/util";
import { isEqual } from "lodash";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { NumberCombo } from "~/components/form/NumberCombo";
import type { OptionalNumeric } from "~/util/string";

export type DosageFieldProps = {
    value: DosageData | null | undefined;
    onUpdateDosage: (dosage: DosageData) => void;
    disabled?: boolean;
    label?: string;
};

const MEDICATION_FORMS = Object.entries(MedicationFormMap) as MapEntry<MedicationForm>[];
const MEDICATION_VOLUMES = Object.entries(MedicationVolumeMap) as MapEntry<MedicationVolume>[];

const DEFAULT_PAYLOAD = {
    form: MedicationForm.capsule,
    amount: null,
    volume: MedicationVolume.milligram,
};

export const DosageField = ({ value, onUpdateDosage, disabled, label = "Medication form" }: DosageFieldProps) => {
    const current = {
        ...DEFAULT_PAYLOAD,
        ...value,
    };

    const update = <T extends keyof DosageData>(key: T, val: DosageData[T]) => {
        const payload = {
            ...current,
            [key]: val,
        };

        if (!isEqual(payload, current)) {
            onUpdateDosage(payload);
        }
    };

    const { form, amount, volume } = current;
    const setForm = (value: MedicationForm) => update("form", value);
    const setAmount = (value: OptionalNumeric) => update("amount", value);
    const setVolume = (value: MedicationVolume) => update("volume", value);

    return (
        <>
            <ComboBox<MedicationForm, MapEntry<MedicationForm>>
                value={form}
                onItemSelected={setForm}
                items={MEDICATION_FORMS}
                itemMap={MedicationFormMap}
                disabled={disabled}
                label={label}
            />
            <NumberCombo<MedicationVolume>
                label="Dosage strength"
                number={amount}
                setNumber={setAmount}
                combo={volume}
                setCombo={setVolume}
                disabled={disabled}
                optional
                comboProps={{
                    items: MEDICATION_VOLUMES,
                    label: "Volume",
                }}
            />
        </>
    );
};
